import React from "react";
import { useSelector } from "react-redux";

// Every table on the report shares one fixed column layout, so the service
// tables and the grand total row all line up on the same grid.
// Sized so each column fits its widest value ("SA/2026/00100", the "ItemQty"
// header, "134,312.50") without spilling into its neighbour.
const columnWidths = ["10%", "12%", "16%", "23%", "8%", "8%", "9%", "14%"];

const ReportColumns = () => (
  <colgroup>
    {columnWidths.map((width, index) => (
      <col key={index} style={{ width }} />
    ))}
  </colgroup>
);

const reportTableStyle = {
  width: "100%",
  borderRadius: 1,
  tableLayout: "fixed",
};

// Keeps html2pdf from slicing a row in half across a page boundary.
const noBreakStyle = {
  pageBreakInside: "avoid",
  breakInside: "avoid",
};

const headerCellStyle = {
  overflowWrap: "break-word",
  padding: "3.5px 5px",
  fontWeight: 500,
  fontSize: 15,
  backgroundColor: "#FDFEFF",
  borderBottom: "2px solid #C8C8C8",
  borderTop: "2px solid #C8C8C8",
  textAlign: "left",
};

const bodyCellStyle = {
  overflowWrap: "break-word",
  padding: "5.5px 5px",
  fontSize: 15,
  fontWeight: 400,
  textAlign: "left",
  verticalAlign: "top",
  borderBottom: "0.5px solid #C8C8C8",
};

const totalCellStyle = {
  overflowWrap: "break-word",
  padding: "3.5px 5px",
  fontWeight: 500,
  fontSize: 15,
  backgroundColor: "#FDFEFF",
  borderBottom: "0.5px solid #C8C8C8",
  borderTop: "0.5px solid #C8C8C8",
  textAlign: "left",
};

const parseAmount = (value) =>
  Number(String(value ?? "").replace(/[^0-9.-]/g, "")) || 0;

const formatAmount = (value) =>
  `₹ ${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// An estimate's amount lives on its items; the estimate row itself is a header.
const getEstimateTotal = (estimate) =>
  estimate?.items?.length
    ? estimate.items.reduce((sum, item) => sum + parseAmount(item?.total), 0)
    : parseAmount(estimate?.total);

const getServiceTotal = (service) =>
  (service?.estimates ?? []).reduce(
    (sum, estimate) => sum + getEstimateTotal(estimate),
    0,
  );

// Quantity follows the same rule as the amount: it lives on the items.
const getEstimateQuantity = (estimate) =>
  estimate?.items?.length
    ? estimate.items.reduce(
        (sum, item) => sum + parseAmount(item?.item_quantity),
        0,
      )
    : parseAmount(estimate?.item_quantity);

const getServiceQuantity = (service) =>
  (service?.estimates ?? []).reduce(
    (sum, estimate) => sum + getEstimateQuantity(estimate),
    0,
  );

const EstimateReport = React.forwardRef(({ estimateRef }, ref) => {
  const { supportingPrintData } = useSelector((state) => state.staffBilling);
  const Data = supportingPrintData?.result ?? [];
  const Total = supportingPrintData?.grand_total ?? {};
  const isSupplierReport = supportingPrintData?.address_type === 1;
  // grand_total.item_quantity comes back empty, so total it from the services.
  const grandQuantity = Data.reduce(
    (sum, service) => sum + getServiceQuantity(service),
    0,
  );

  function getCurrentDateIndianFormat() {
    const today = new Date();
    return `${today.getDate().toString().padStart(2, "0")}/${(
      today.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${today.getFullYear()}`;
  }
  const currentDate = getCurrentDateIndianFormat();

  // The estimate row is the first line of a cell, its items are the lines below it.
  const getCellLines = (estimate) => [
    {
      supplier: isSupplierReport ? estimate?.shop : estimate?.supplier,
      item_quantity: estimate?.item_quantity,
      carat: estimate?.carat,
      price: estimate?.price,
      total: estimate?.total,
    },
    ...(estimate?.items ?? []),
  ];

  const renderLines = (estimate, field) => {
    const lines = getCellLines(estimate);
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line?.[field]}
        {index !== lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <div className="pdf-print-surface" ref={estimateRef}>
      <table
        border={0}
        cellSpacing={0}
        role="presentation"
        cellPadding={0}
        style={{
          backgroundColor: "#ffffff",
          maxWidth: "95%",
          width: "100%",
          borderCollapse: "collapse",
          margin: "0 auto",
          fontSize: 20,
        }}
      >
        <tbody>
          <tr>
            <td
              style={{
                textAlign: "center",
                fontSize: 20,
                textTransform: "upper case",
                fontWeight: 600,
                padding: "3px 0",
              }}
            >
              ESTIMATE BILL REPORT
            </td>
          </tr>
          <tr>
            <td
              style={{
                textAlign: "right",
                fontSize: 12,
                textTransform: "upper case",
                fontWeight: 400,
                padding: "3px 0",
              }}
            >
              Date :{currentDate}
            </td>
          </tr>
          <tr>
            <td
              style={{
                textAlign: "center",
                fontSize: 15,
                textTransform: "upper case",
                fontWeight: 400,
                padding: "3px 0",
                innerHeight: 20,
              }}
            >
              {isSupplierReport
                ? `Supplier Name : ${supportingPrintData?.supplier_name}`
                : `Shop Name : ${supportingPrintData?.shop_name}`}
            </td>
          </tr>

          <tr>
            <td style={{ verticalAlign: "top" }}>
              <table
                border={0}
                cellSpacing={0}
                role="presentation"
                cellPadding={0}
                style={{
                  width: "100%",
                }}
              >
                <tbody>
                  <tr>
                    <td style={{ padding: "15px 0px" }}>
                      {Data.map((service, serviceIndex) => (
                        <table
                          key={service?.service_id ?? serviceIndex}
                          cellSpacing={0}
                          role="presentation"
                          cellPadding={0}
                          style={{ ...reportTableStyle, marginBottom: 20 }}
                        >
                          <ReportColumns />
                          <thead>
                            {/* <tr>
                              <th
                                colSpan={8}
                                style={{
                                  padding: "3.5px 5px",
                                  fontWeight: 600,
                                  fontSize: 16,
                                  textAlign: "left",
                                  textTransform: "uppercase",
                                }}
                              >
                                {service?.service_name}
                              </th>
                            </tr> */}
                            <tr style={noBreakStyle}>
                              <th style={headerCellStyle}>Date</th>
                              <th style={headerCellStyle}>Est. No</th>
                              <th style={headerCellStyle}>GLI No</th>
                              <th style={headerCellStyle}>
                                {isSupplierReport
                                  ? "Shop / Agent"
                                  : "Supplier / Agent"}
                              </th>
                              <th style={headerCellStyle}>
                                Item
                                <br />
                                Qty
                              </th>
                              <th style={headerCellStyle}>Cts</th>
                              <th style={headerCellStyle}>Price</th>
                              <th
                                style={{
                                  ...headerCellStyle,
                                  textAlign: "right",
                                }}
                              >
                                Total Amt
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {(service?.estimates ?? []).map(
                              (estimate, estimateIndex) => (
                                <tr
                                  key={
                                    estimate?.estimate_number ?? estimateIndex
                                  }
                                  style={noBreakStyle}
                                >
                                  <td style={bodyCellStyle}>
                                    {estimate?.date}
                                  </td>
                                  <td style={bodyCellStyle}>
                                    {estimate?.estimate_number}
                                  </td>
                                  <td style={bodyCellStyle}>
                                    {estimate?.gli_number}
                                  </td>
                                  <td style={bodyCellStyle}>
                                    {renderLines(estimate, "supplier")}
                                  </td>
                                  <td style={bodyCellStyle}>
                                    {renderLines(estimate, "item_quantity")}
                                  </td>
                                  <td style={bodyCellStyle}>
                                    {renderLines(estimate, "carat")}
                                  </td>
                                  <td style={bodyCellStyle}>
                                    {renderLines(estimate, "price")}
                                  </td>
                                  <td
                                    style={{
                                      ...bodyCellStyle,
                                      textAlign: "right",
                                    }}
                                  >
                                    {renderLines(estimate, "total")}
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>

                          {/* per service total */}
                          <tbody>
                            <tr style={noBreakStyle}>
                              <td style={totalCellStyle}></td>
                              <td style={totalCellStyle}></td>
                              <td style={totalCellStyle}></td>
                              <td
                                style={{
                                  ...totalCellStyle,
                                  fontSize: 16,
                                  textTransform: "uppercase",
                                }}
                              >
                                Total
                              </td>
                              <td style={{ ...totalCellStyle, fontSize: 16 }}>
                                {getServiceQuantity(service)}
                              </td>
                              <td
                                colSpan={3}
                                style={{
                                  ...totalCellStyle,
                                  fontSize: 16,
                                  textAlign: "right",
                                }}
                              >
                                {formatAmount(getServiceTotal(service))}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      ))}

                      {/* grand total */}
                      <table
                        cellSpacing={0}
                        role="presentation"
                        cellPadding={0}
                        style={reportTableStyle}
                      >
                        <ReportColumns />
                        <thead>
                          <tr style={noBreakStyle}>
                            <th style={totalCellStyle}></th>
                            <th style={totalCellStyle}></th>
                            <th style={totalCellStyle}></th>
                            <th
                              style={{
                                ...totalCellStyle,
                                fontSize: 18,
                                textTransform: "uppercase",
                              }}
                            >
                              Grand Total
                            </th>
                            <th style={{ ...totalCellStyle, fontSize: 18 }}>
                              {grandQuantity}
                            </th>
                            <th
                              colSpan={3}
                              style={{
                                ...totalCellStyle,
                                fontSize: 18,
                                textAlign: "right",
                              }}
                            >
                              {Total?.total}
                            </th>
                          </tr>
                        </thead>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
});

export default EstimateReport;
